import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown } from 'lucide-react';

const colorMap = {
  blue: {
    circle: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    text: 'text-blue-600 dark:text-blue-400',
  },
  green: {
    circle: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    text: 'text-emerald-600 dark:text-emerald-400',
  },
  red: {
    circle: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    text: 'text-red-600 dark:text-red-400',
  },
  yellow: {
    circle: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    text: 'text-amber-600 dark:text-amber-400',
  },
  purple: {
    circle: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    text: 'text-purple-600 dark:text-purple-400',
  },
};

function AnimatedCounter({ value, duration = 1000 }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime = null;
    const startValue = 0;
    const endValue = typeof value === 'number' ? value : parseFloat(value) || 0;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(startValue + (endValue - startValue) * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  return <span>{count.toLocaleString()}</span>;
}

export default function StatCard({
  icon: Icon,
  label,
  value,
  trend,
  trendLabel,
  color = 'blue',
  onClick,
  className = '',
}) {
  const colors = colorMap[color] || colorMap.blue;
  const isTrendUp = trend && trend > 0;
  const trendColor = isTrendUp ? 'text-emerald-600 dark:text-emerald-400' : trend < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500';

  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: '0 12px 30px rgba(0,0,0,0.08)' }}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm ${onClick ? 'cursor-pointer' : ''} ${className}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`flex items-center justify-center w-11 h-11 rounded-xl ${colors.circle}`}>
          {Icon && <Icon size={22} />}
        </div>
        {trend !== undefined && trend !== null && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trendColor}`}>
            {isTrendUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">
        <AnimatedCounter value={value} />
      </p>
      {trendLabel && (
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{trendLabel}</p>
      )}
    </motion.div>
  );
}
